import { useState } from "react";
import generateDefaultValue from "../../utils/GenerateDefaultValue";
import assignDataToMap from "../../utils/AssignDataToMap";
import generateEditorElement from "../../utils/GenerateEditorElement";
import useAPIRequest from "../simple/useAPIRequest";
import validatorUtil from "../../utils/InputValidator";

const useEditorControl = ({
  configs,
  isForm,
  apiName,
  apiSuffix,
  customElements,
}) => {
  const [error, setError] = useState({});

  const [value, setValue] = useState(
    generateDefaultValue({ tableConfigs: configs })
  );

  const { requestPost, requestGetOne, requestGet, loading } = useAPIRequest({
    url: `api/${apiName}`,
  });

  const handleInputChange = (field, newValue) => {
    const newMap = { ...value };
    if (!field.includes(".")) {
      newMap[field] = newValue;
    } else {
      assignDataToMap({ key: field, map: newMap, value: newValue });
    }
    setValue(newMap);
  };

  const handleMultipleInputChange = (mapsNewValue) => {
    const newMap = { ...value };
    for (const mapNewValue of mapsNewValue) {
      newMap[mapNewValue.field] = mapNewValue.newValue;
    }
    setValue(newMap);
  };

  const generateSelectorDialog = ({ field, componentFunction }) => {
    let fieldArr = [];
    if (field.includes(" ")) {
      fieldArr = field.split(" ");
    } else if (field.includes(",")) {
      fieldArr = field.split(",");
    } else {
      fieldArr.push(field);
    }
    for (const fieldSingle of fieldArr) {
      const config = configs.find((element) => element.field === fieldSingle);
      if (config) {
        config.selectorGenerator = componentFunction;
      }
    }
  };

  //auto generate element
  const defaultProps = {
    value: value,
    error: error,
    handleInputChange: handleInputChange,
    handleMultipleInputChange: handleMultipleInputChange,
  };

  const mapGeneratedElement = {};
  let firstElement = true;
  for (const config of configs) {
    let foundEl;
    for (const custEl of customElements) {
      const { field, element } = custEl;
      if (config.field === field) {
        foundEl = element;
      }
    }
    if (foundEl) {
      mapGeneratedElement[config.field] = foundEl({
        ...defaultProps,
        autoFocus: firstElement,
        config: config,
      });
    } else {
      const generatedElement = generateEditorElement({
        ...defaultProps,
        autoFocus: firstElement,
        config: config,
      });
      if (generatedElement) {
        mapGeneratedElement[config.field] = generatedElement;
      }
    }
    if (firstElement) {
      firstElement = false;
    }
  }

  const onFetchProfileData = async ({ id, onError, onSuccess }) => {
    await requestGetOne({
      apiUrl: `profile`,
      onError: ({ message, data }) => {
        onError({ message, data });
      },
      onSuccess: ({ message, data }) => {
        onSuccess({ message, data });
      },
    });
  };

  const onProfileSave = async ({ newValue, onError, onSuccess }) => {
    await requestPost({
      apiUrl: "updateprofile",
      body: newValue,
      isForm: isForm,
      onError: ({ message, data }) => {
        onError({ message: message, data: data });
      },
      onSuccess: ({ data, message }) => {
        onSuccess({ data, message });
      },
    });
  };

  const onFetchData = async ({ id, onError, onSuccess }) => {
    await requestGetOne({
      apiUrl: `one/${id}`,
      onError: ({ message, data }) => {
        onError({ message, data });
      },
      onSuccess: ({ message, data }) => {
        onSuccess({ message, data });
      },
    });
  };

  const onFetchListData = async ({ id, onError, onSuccess }) => {
    await requestGet({
      apiUrl: `data`,
      onError: ({ message, data }) => {
        onError({ message, data });
      },
      onSuccess: ({ message, data }) => {
        onSuccess({ message, data });
      },
    });
  };

  const onSave = async ({ newValue, onError, onSuccess }) => {
    await requestPost({
      apiUrl: apiSuffix ?? "save",
      body: newValue,
      isForm: isForm,
      onError: ({ message, data }) => {
        onError({ message: message, data: data });
      },
      onSuccess: ({ data, message }) => {
        onSuccess({ data, message });
      },
    });
  };

  const validator = ({
    configs,
    value,
    onError,
    isEmpty,
    isEmptyMessage,
    mustEqual,
    mustEqualMessage,
  }) => {
    return validatorUtil({
      configs,
      value,
      onError,
      isEmpty,
      isEmptyMessage,
      mustEqual,
      mustEqualMessage,
    });
  };

  return {
    // methods
    onSave,
    onFetchData,
    onFetchListData,
    onProfileSave,
    onFetchProfileData,
    generateSelectorDialog,
    handleInputChange,
    handleMultipleInputChange,
    // states
    value,
    setValue,
    error,
    setError,
    loading,
    elements: mapGeneratedElement,
    //utils
    validator,
  };
};

export default useEditorControl;
